import { set } from "date-fns";
import { endOfDay } from "date-fns";
import { z } from "zod";

export const formSchema = z.object({
	goalValue: z.number().nullable(),
	incrementValue: z.number().nullable(),
	isIncrement: z.boolean(),
	markComplete: z.boolean(),
	message: z.string(),
	asOfDate: z
		.date()
		.nullable()
		.refine((date) => !date || date <= endOfDay(new Date()), {
			message: "Date cannot be in the future",
		}),
	outlookId: z.string().nullable(),
});

export type FormValues = z.infer<typeof formSchema>;

export type GoalUpdate = { field: string; value: unknown };

type GoalSnapshot = {
	value?: number | null;
	color?: { _id: string } | null;
};

// Builds the PATCH payloads for an Update Progress submission. Returns a list because
// "value + status" can't be combined in a single PATCH (backend rejects it), so when the
// user changes the value AND marks complete we have to split into two sequential calls.
// When marking complete with no value/outlook change, value and color are omitted so the
// resulting history entry matches CompleteGoalModal's.
export const buildSubmitPayloads = ({
	formValues,
	goal,
	currentTime,
}: {
	formValues: FormValues;
	goal: GoalSnapshot;
	currentTime: Date;
}): GoalUpdate[][] => {
	const newValue =
		formValues.isIncrement && formValues.incrementValue !== null
			? (goal.value ?? 0) + formValues.incrementValue
			: formValues.goalValue;
	const newColor = formValues.outlookId && formValues.outlookId !== "none" ? formValues.outlookId : null;
	const newDate = formValues.asOfDate
		? set(formValues.asOfDate, {
				hours: currentTime.getHours(),
				minutes: currentTime.getMinutes(),
				seconds: currentTime.getSeconds(),
			}).toISOString()
		: null;

	const currentColorId = goal.color?._id ?? null;
	const skipValue = formValues.markComplete && newValue !== null && newValue !== undefined && newValue === goal.value;
	const skipColor = formValues.markComplete && newColor === currentColorId;

	const updates: GoalUpdate[] = [];

	if (!skipValue && newValue !== null && newValue !== undefined) {
		updates.push({ field: "value", value: newValue });
	}

	if (formValues.message) {
		updates.push({ field: "message", value: formValues.message });
	}

	if (newDate) {
		updates.push({ field: "as_of", value: newDate });
	}

	if (!skipColor) {
		if (newColor) {
			updates.push({ field: "color", value: newColor });
		} else if (goal.color) {
			updates.push({ field: "color", value: null });
		}
	}

	const includesValueChange = updates.some((u) => u.field === "value");

	if (formValues.markComplete && !includesValueChange) {
		return [[...updates, { field: "status", value: "done" }]];
	}

	const payloads: GoalUpdate[][] = [];
	if (updates.length > 0) {
		payloads.push(updates);
	}
	if (formValues.markComplete) {
		payloads.push([{ field: "status", value: "done" }]);
	}
	return payloads;
};
